/* eslint-disable no-console */
import { Sound, Music, SoundType } from 'types';
import { SFX, SFX_PATH, MUSIC_PATH, EXTENSION_ID } from 'constants/audio';
import { BASE_SOUND, MUSIC_SOUND } from 'models/sound';

type Sounds = { [name: string]: Sound | Music };

const isMusic = (item: Sound | Music): item is Music => item.hasOwnProperty('lazy');

class SoundsManager {
  private context: AudioContext;
  private sounds: Sounds = {};

  constructor(context: AudioContext) {
    this.context = context;
  }

  public add(type: SoundType, sounds: { [name: string]: Partial<Sound | Music> }) {
    const extension = this.extension;
    const path = type === SFX ? SFX_PATH : MUSIC_PATH;
    const BASE = type === SFX ? BASE_SOUND : MUSIC_SOUND;

    Object.entries(sounds).forEach(([name, data]) => {
      const sound = { ...BASE, ...data };
      sound.name = name;
      sound.type = sound.type || type;
      if (sound.url) {
        sound.url = `${ path }${ sound.url }.${ extension }`;
      }

      this.sounds[name] = sound;
    });
  }
  public get(name: string): Sound | Music | null {
    if (!this.sounds.hasOwnProperty(name)) return null;

    return this.sounds[name];
  }

  public loadAll() {
    const promises: Array<Promise<any>> = [];
    Object.values(this.sounds).forEach((sound) => {
      if (isMusic(sound) && sound.lazy) return;

      promises.push(this.load(sound));
    });

    return Promise.all(promises);
  }
  public load(sound: Sound): Promise<any> {
    const { name, url = null } = sound;
    if (!this.sounds.hasOwnProperty(name) || !url) {
      return Promise.resolve(null);
    }

    if (sound.hasOwnProperty('promise')) {
      return sound.promise!;
    }

    sound.promise = this.fetch(url)
      .then(this.decode)
      .then((buffer) => {
        sound.buffer = buffer;
        return true;
      })
      .catch((error) => {
        // tslint:disable-next-line no-console
        console.error('Error while loading and decoding sound', error);
        throw new Error(error.message);
      });

    return sound.promise;
  }

  private fetch(url: string): Promise<any> {
    const request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.responseType = 'arraybuffer';

    return new Promise((resolve, reject) => {
      request.onload = () => resolve(request.response);
      request.onerror = reject;
      request.send();
    });
  }
  private decode = (response: any): Promise<AudioBuffer> =>
    new Promise((resolve, reject) => this.context.decodeAudioData(response, resolve, reject))

  get extension() {
    const audioElement = document.getElementById(EXTENSION_ID) as HTMLAudioElement;
    if (!audioElement || !audioElement.currentSrc) return 'ogg';

    const url = audioElement.currentSrc;
    return url.includes('.m4a') ? 'm4a' : 'ogg';
  }
}

export default SoundsManager;
